'use client';

import { useEffect, useRef, useState } from 'react';
import { ArrowRight, CheckCircle2, Download } from 'lucide-react';
import { MARKETING_TOKENS as T, SUPPORT_EMAIL } from '@/components/marketing/marketingTokens';
import Turnstile from '@/components/auth/Turnstile';
import { readAttribution } from '@/lib/attribution';
import { trackLead } from '@/lib/analytics';
import { CONTACT_COPY_EN, type ContactCopy } from './contactCopy';
import { leadAssetBySlug, type LeadAsset } from '@/lib/leadAssets';

const SERIF = 'var(--font-serif), Georgia, "Times New Roman", serif';

// Field focus/hover + submit hover — inline styles can't express these. Scoped
// to this form; keys off the marketing --mk-* vars.
const CF_CSS = `
  /* Fields sit on the SURFACE, not on surface-alt.
     A grey input on a grey card gave every field the same weight as the card
     holding it — the whole form read as one flat slab, which is what made the
     page feel washed out. White fields on a white card, separated by a border,
     put the typing surface in front of everything else on the page. */
  .cf-field{background:var(--mk-surface);color:var(--mk-text);border:1px solid var(--mk-border);border-radius:10px;transition:border-color .15s ease,box-shadow .15s ease,background .15s ease;width:100%;}
  .cf-field:hover:not(:focus){border-color:var(--mk-primary);}
  /* A 3px ring rather than 1px: at 1px the focus state was a border colour
     change and nothing more, which is easy to miss and fails as an
     accessibility affordance. */
  .cf-field:focus{outline:none;border-color:var(--mk-primary);box-shadow:0 0 0 3px rgba(var(--mk-primary-rgb),0.16);}
  .cf-field::placeholder{color:var(--mk-muted);}
  .cf-submit{transition:background .2s ease,box-shadow .2s ease;background:var(--mk-primary);box-shadow:0 8px 20px rgba(var(--mk-primary-rgb),0.25);}
  .cf-submit:hover:not(:disabled){background:var(--mk-primary-dark);box-shadow:0 12px 28px rgba(var(--mk-primary-rgb),0.32);}
  .cf-submit:disabled{opacity:.6;cursor:default;}
  .cf-submit .l-arrow{transition:transform .2s ease;} .cf-submit:hover:not(:disabled) .l-arrow{transform:translateX(3px);}
  /* The honeypot. Off-screen rather than display:none or hidden — a bot that
     filters on either of those skips it, which defeats the point. Kept out of
     the tab order and out of the accessibility tree instead, so no real
     visitor (sighted, screen-reader or keyboard-only) can land on it. */
  .cf-hp{position:absolute;left:-9999px;top:auto;width:1px;height:1px;overflow:hidden;}
`;

function errMsg(e: unknown, fb: string) {
  return (e as { response?: { data?: { message?: string } } })?.response?.data?.message || fb;
}

/**
 * `req` renders the asterisk. Everything except the message is required now, so
 * the marks and the validation finally agree — a field that blocks submission
 * without saying so is the most annoying kind of form.
 */
const Label = ({ htmlFor, children, req }: { htmlFor: string; children: React.ReactNode; req?: boolean }) => (
  <label htmlFor={htmlFor} className="mb-1.5 block text-[12.5px] font-semibold" style={{ color: T.TEXT_MUTED }}>
    {children}
    {req && <span aria-hidden className="ml-0.5" style={{ color: T.PRIMARY }}>*</span>}
  </label>
);

/**
 * The contact form — the only lead-capture surface on the site.
 *
 * `copy` makes it bilingual without a second implementation; see contactCopy.ts
 * for why that matters. `lang` only tags the rendered markup.
 */
export default function ContactForm({
  copy = CONTACT_COPY_EN,
  lang = 'en',
  variant = 'full',
  assetSlug,
  presetInterest,
  from,
}: {
  copy?: ContactCopy;
  lang?: 'en' | 'ta';
  /**
   * 'download' is the compact form a guide embeds beside its sheet: the four
   * fields the server actually requires, a three-line explanation of what
   * pressing the button does, and nothing else.
   *
   * It exists because the guide block used to be a LINK to /contact, and a
   * reader who came for a blank work order format does not read "Get the
   * format" followed by a contact page and conclude that the contact page is
   * the download. They conclude they clicked the wrong thing. The form has to
   * be where the offer is, and it has to say what it does before they type.
   *
   * Same endpoint, same honeypot, same timing check, same captcha — this is a
   * narrower set of INPUTS, never a weaker set of defences.
   */
  variant?: 'full' | 'download';
  /** Preselect the sheet without a `?asset=` round trip through the URL. */
  assetSlug?: string;
  presetInterest?: string;
  /** Names the page this form is embedded in, for attribution. */
  from?: string;
} = {}) {
  const compact = variant === 'download';
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  // Mounted immediately, unlike the auth pages: a spam submission to this form
  // succeeds like a real one, so there is no failure signal to gate on and the
  // only moment to ask is every time. Managed mode resolves silently for
  // almost every genuine visitor.
  const [captchaToken, setCaptchaToken] = useState('');
  const [captchaNonce, setCaptchaNonce] = useState(0);
  /**
   * The widget could not be put on screen at all — no site key in this build,
   * or Cloudflare's script blocked. Reported to the server so the enquiry is
   * accepted and flagged rather than refused with an instruction the reader
   * cannot follow ("complete the security check", with nothing to complete).
   * See Turnstile.tsx and Builder-API/controllers/contactController.js.
   */
  const [captchaUnavailable, setCaptchaUnavailable] = useState(false);
  const [form, setForm] = useState({
    name: '', email: '', company: '', phone: '',
    /**
     * `other`, not `demo`, for a download: somebody who asked for a file has
     * not asked for a demo, and recording them as one makes the pipeline lie.
     * `sites` is left blank rather than defaulted — the compact form does not
     * ask, and answering on the reader's behalf would be inventing data.
     */
    interest: presetInterest || (compact ? 'other' : 'demo'),
    sites: compact ? '' : '1-3',
    message: '',
    /**
     * The honeypot. Part of form state so React owns it like any other field.
     *
     * NOT named `website` / `url` / `company_url`. Password managers and
     * browser autofill actively look for those names and will fill them on a
     * real visitor's behalf — and a filled honeypot discards the enquiry while
     * telling the sender it worked, which is precisely the invisible-loss class
     * the captcha fallback was written to end. `referenceCode` is a name a
     * form-stuffing bot will still fill (it fills everything) and no autofill
     * heuristic targets.
     */
    referenceCode: '',
  });
  /**
   * When the form first appeared. Sent as `fillMs` and used by the server ONLY
   * to qualify a captcha-less submission: five fields typed by a person take
   * seconds, a script posts in milliseconds. A ref, not state — it must not
   * re-render anything, and it must survive every keystroke.
   *
   * Stamped in an effect rather than during render: `Date.now()` in a render
   * body is impure, and the effect fires at the moment the form is actually on
   * screen, which is the event being measured anyway. 0 until then, and the
   * server treats a 0/absent duration as plausible rather than suspicious.
   */
  const mountedAt = useRef<number>(0);
  useEffect(() => {
    if (mountedAt.current === 0) mountedAt.current = Date.now();
  }, []);

  /**
   * The asset the reader came for, from `?asset=` — set by a guide's lead block.
   * Resolved against the local registry rather than trusted as a path, so the
   * URL cannot make this page offer an arbitrary file.
   */
  const [asset, setAsset] = useState<LeadAsset | undefined>(() => leadAssetBySlug(assetSlug));

  // Preset the interest from ?interest= after mount (not at init) so SSR and the
  // first client render match — the URL isn't readable during SSR. Every CTA on
  // the site now sets it (src/lib/contactHref.ts), so this is the common path
  // rather than a rarity.
  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    const i = q.get('interest');
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (i && copy.interests.some((x) => x.value === i)) setForm((f) => ({ ...f, interest: i }));
    // A prop wins over the URL: an embedded form already knows its sheet, and
    // a stray ?asset= on the guide's own URL must not swap it for another.
    if (!assetSlug) {
      const a = leadAssetBySlug(q.get('asset'));
      if (a) setAsset(a);
    }
  }, [copy, assetSlug]);

  const onChange = (key: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setForm((f) => ({ ...f, [key]: e.target.value }));
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    // Same floor the server applies (controllers/contactController.js): a
    // two-digit "phone number" is a slip, and catching it here means the
    // sender keeps what they typed instead of a round trip to be told no.
    if (form.phone.replace(/\D/g, '').length < 7) {
      setError(copy.errors.shortPhone);
      return;
    }
    setSubmitting(true);
    /**
     * An embedded form was never CLICKED through to, so there is no `?from=` in
     * the URL to read — without this, every lead from a guide would arrive with
     * a blank `cta_from` and the one question these pages exist to answer
     * (which guide produced an enquiry) would have no answer.
     */
    const read = readAttribution();
    const attribution = from && 'ctaFrom' in read ? { ...read, ctaFrom: read.ctaFrom || from } : read;
    try {
      /**
       * axios, fetched at submit rather than at import.
       *
       * This form is embedded in all eleven guides and both contact pages, so a
       * static import put the HTTP client (~20 KB gzipped) into the first load
       * of thirteen content pages whose readers mostly never submit anything.
       * Here it is awaited inside a handler that is already async and already
       * about to wait on a network round trip, so the chunk arrives alongside
       * the request it exists to make and nobody waits on it twice.
       *
       * A failed chunk load rejects into the same catch as a failed POST, which
       * shows the generic error and resets the single-use captcha — the right
       * behaviour either way.
       */
      const { default: api } = await import('@/utils/api');
      await api.post('/contact', {
        ...form,
        captchaToken,
        captchaUnavailable,
        fillMs: mountedAt.current ? Date.now() - mountedAt.current : undefined,
        // Which sheet they asked for (slug only — the API owns the link, see
        // lib/leadAssets.ts) and which language they are reading in, so the
        // confirmation email and the person who calls both match the page.
        asset: asset?.slug,
        lang,
        attribution,
      });
      setSubmitted(true);
      /**
       * The conversion, reported to GA4 AFTER the server accepted it — an event
       * fired on click would count enquiries that never arrived. No name, email
       * or phone is sent: see lib/analytics.ts.
       */
      const ft = 'firstTouch' in attribution ? attribution.firstTouch : undefined;
      trackLead({
        interest: form.interest,
        asset: asset?.slug || '',
        lang,
        sites: form.sites,
        lead_landing_page: ft?.path || (typeof window !== 'undefined' ? window.location.pathname : ''),
        cta_from: ('ctaFrom' in attribution ? attribution.ctaFrom : '') || from || '',
      });
    } catch (err) {
      setError(errMsg(err, copy.errors.generic.replace('{email}', SUPPORT_EMAIL)));
      // Single-use token — clear it or a retry posts a value the server has
      // already spent and the form fails a second time for the wrong reason.
      setCaptchaToken('');
      setCaptchaNonce((n) => n + 1);
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div
        className={compact ? 'rounded-md p-7 text-center' : 'rounded-md p-8 text-center md:p-12'}
        style={{
          border: `1px solid ${T.BORDER}`,
          backgroundColor: T.SURFACE,
          // Inside a guide the panel already sits in a bordered aside; the big
          // drop shadow made it read as a modal that had failed to centre.
          boxShadow: compact ? 'none' : '0 30px 60px -30px rgba(28,26,23,0.16)',
        }}
      >
        <div className="mx-auto mb-4 flex h-[60px] w-[60px] items-center justify-center rounded-full" style={{ backgroundColor: 'rgba(21,128,61,0.10)', color: '#15803d' }}>
          <CheckCircle2 className="h-8 w-8" />
        </div>
        <h2 className="mb-1 text-[26px]" style={{ fontFamily: SERIF, fontWeight: 600, letterSpacing: '-0.015em', color: T.TEXT }}>{copy.success.title}</h2>
        <p className="mx-auto max-w-[420px] text-[14.5px] leading-[1.65]" style={{ color: T.TEXT_MUTED }}>
          {/* An existing customer who asked for support is not waiting to be
              sold a demo. Now that every CTA sets ?interest=, this branch is
              actually reachable — before, every submission read as 'demo'. */}
          {asset ? copy.success.bodyAsset.replace('{asset}', asset.label)
            : form.interest === 'support' ? copy.success.bodySupport
              : copy.success.body}
        </p>
        {/* The file is handed over HERE, not only by email.
            Making somebody wait for an inbox to see the thing they just asked
            for is how a download offer becomes a bounce — and the email copy
            carries the same link, so a lost tab costs nothing either. */}
        {asset && (
          <a
            href={asset.file}
            download
            className="mt-5 inline-flex items-center gap-2 rounded-full px-6 py-3 text-[14px] font-semibold no-underline"
            style={{ background: T.PRIMARY, color: T.ON_PRIMARY }}
          >
            <Download className="h-4 w-4" />
            {copy.success.download}
          </a>
        )}
      </div>
    );
  }

  // 16px on phones so iOS Safari doesn't zoom the page on focus; 14px from sm up.
  // h-12, not h-11 — a 44px target is the floor for touch, and the extra
  // height is most of what separates "premium form" from "admin form".
  /**
   * 16px up to the DESKTOP breakpoint, not the small one.
   *
   * `sm:` is 640px, so an iPad at 768px got 14.5px text — and iOS zooms the
   * whole page when a focused field is under 16px. A tablet is a touch device
   * with a wide screen; the font only shrinks where there is a real keyboard.
   */
  const input = 'cf-field h-12 px-4 text-[16px] lg:text-[14.5px]';
  const textarea = 'cf-field px-4 py-3 text-[16px] sm:text-[14.5px]';

  return (
    <form
      onSubmit={onSubmit}
      lang={lang === 'ta' ? 'ta' : undefined}
      className={compact ? 'rounded-md p-5 md:p-6' : 'rounded-md p-6 md:p-8'}
      style={{
        border: `1px solid ${T.BORDER}`,
        backgroundColor: T.SURFACE,
        boxShadow: compact ? 'none' : '0 30px 60px -30px rgba(28,26,23,0.12)',
      }}
    >
      <style dangerouslySetInnerHTML={{ __html: CF_CSS }} />
      <div className={compact ? 'flex flex-col gap-4' : 'flex flex-col gap-5'}>
        {/* The form had no heading at all — it opened straight onto "Full name",
            so the eye landed on a label rather than on what the block is for.
            One line, and it also carries the only instruction that matters. */}
        <div>
          <h2 className={compact ? 'text-[17px]' : 'text-[19px]'} style={{ fontFamily: 'var(--font-serif), Georgia, serif', fontWeight: 600, letterSpacing: '-0.01em', color: T.TEXT }}>
            {compact ? copy.download.title : copy.formTitle}
          </h2>
          <p className="mt-1 text-[13px]" style={{ color: T.TEXT_MUTED }}>
            {compact
              ? copy.download.hint.replace('{asset}', asset?.label || 'file')
              : copy.formHint}
          </p>
        </div>

        {/* The three lines that stop the form being a surprise.
            A reader who came for a blank format has to be told, before they
            type anything, that the form IS the download and that the file
            arrives here rather than in a sales call next week. Numbered
            because it is a sequence, and ordered so the reassurance ("a copy
            by email") lands last. */}
        {compact && (
          <ol className="m-0 flex list-none flex-col gap-2 p-0">
            {copy.download.steps.map((step, i) => (
              <li key={step} className="flex items-start gap-2.5 text-[13.5px] leading-[1.5]" style={{ color: T.TEXT_MUTED }}>
                <span
                  aria-hidden
                  className="mt-[1px] inline-flex h-[20px] w-[20px] flex-none items-center justify-center rounded-full text-[11px] font-bold"
                  style={{ background: 'rgba(var(--mk-primary-rgb),0.10)', color: T.PRIMARY }}
                >
                  {i + 1}
                </span>
                {step}
              </li>
            ))}
          </ol>
        )}
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <Label htmlFor="cf-name" req>{copy.labels.name}</Label>
            <input id="cf-name" required name="name" value={form.name} onChange={onChange('name')} className={input} />
          </div>
          <div>
            <Label htmlFor="cf-email" req>{copy.labels.email}</Label>
            <input id="cf-email" required type="email" name="email" value={form.email} onChange={onChange('email')} className={input} />
          </div>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <Label htmlFor="cf-company" req>{copy.labels.company}</Label>
            <input id="cf-company" required name="company" value={form.company} onChange={onChange('company')} className={input} />
          </div>
          <div>
            <Label htmlFor="cf-phone" req>{copy.labels.phone}</Label>
            <input id="cf-phone" required type="tel" name="phone" value={form.phone} onChange={onChange('phone')} className={input} />
          </div>
        </div>
        {!compact && (
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <Label htmlFor="cf-interest">{copy.labels.interest}</Label>
            <select id="cf-interest" value={form.interest} onChange={onChange('interest')} className={input}>
              {copy.interests.map((i) => <option key={i.value} value={i.value}>{i.label}</option>)}
            </select>
          </div>
          <div>
            <Label htmlFor="cf-sites">{copy.labels.sites}</Label>
            <select id="cf-sites" value={form.sites} onChange={onChange('sites')} className={input}>
              {copy.sitesRanges.map((i) => <option key={i.value} value={i.value}>{i.label}</option>)}
            </select>
          </div>
        </div>
        )}
        {!compact && (
        <div>
          <Label htmlFor="cf-message">{copy.labels.message}</Label>
          <textarea id="cf-message" name="message" rows={4} value={form.message} onChange={onChange('message')} className={textarea} />
        </div>
        )}

        {/* Honeypot. `aria-hidden` + `tabIndex={-1}` keep it away from every
            human route into the form; `autoComplete="off"` stops a browser
            helpfully filling it on their behalf, which would silently discard
            a real enquiry. */}
        <div className="cf-hp" aria-hidden>
          <label htmlFor="cf-refcode">{copy.honeypotLabel}</label>
          <input
            id="cf-refcode" name="referenceCode" type="text" tabIndex={-1} autoComplete="off"
            value={form.referenceCode} onChange={onChange('referenceCode')}
          />
        </div>

        {error && <p role="alert" className="text-[13px] font-semibold" style={{ color: '#b91c1c' }}>{error}</p>}
        <Turnstile
          action="contact"
          resetSignal={captchaNonce}
          onToken={setCaptchaToken}
          onUnavailable={() => setCaptchaUnavailable(true)}
        />
        <div className={compact ? 'flex' : 'flex justify-end'}>
          <button
            type="submit"
            disabled={submitting}
            className={`cf-submit inline-flex items-center justify-center gap-2 rounded-full px-6 text-[14px] font-semibold ${compact ? 'h-12 w-full sm:w-auto sm:px-8' : 'py-2.5'}`}
            style={{ color: T.ON_PRIMARY }}
          >
            {/* The download icon, not an arrow: the button ends in a file, and
                an arrow promises another page. */}
            {submitting
              ? (compact ? copy.download.submitting : copy.submitting)
              : compact
                ? <><Download className="h-4 w-4" /> {copy.download.submit}</>
                : <>{copy.submit} <ArrowRight className="l-arrow h-4 w-4" /></>}
          </button>
        </div>
      </div>
    </form>
  );
}
